import { IconLoader2 } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

export type TagSize = "md" | "sm";

// Color variants map 1:1 to Design System fruit palettes. Each is a -50 tint
// background with -800 text (Figma "Tag"): -800 holds AA on every fruit, where
// the -600 the DS once drew fails on seven of them. Pick the variant at the
// call site (e.g. article-type → color via getArticleTypeVariant).
export type TagVariant =
  | "mint"
  | "moss"
  | "pine"
  | "lime"
  | "lemon"
  | "clementine"
  | "strawberry"
  | "pitaya"
  | "blueberry"
  | "blackberry"
  | "slate"
  | "neutral";

// Both sizes share the box (24 high, 4/8 padding) and differ by type only:
// `md` is the desktop chip (13px), `sm` the mobile cards' (11px).
const sizeClasses = {
  md: "gap-1 px-2 py-1 text-footnote leading-4",
  sm: "gap-1 px-2 py-1 text-caption",
} as const satisfies Record<TagSize, string>;

// Full class strings (not interpolated) so Tailwind's JIT keeps every variant.
const variantClasses = {
  mint: "bg-ds-mint-50 text-ds-mint-800",
  moss: "bg-ds-moss-50 text-ds-moss-800",
  pine: "bg-ds-pine-50 text-ds-pine-800",
  lime: "bg-ds-lime-50 text-ds-lime-800",
  lemon: "bg-ds-lemon-50 text-ds-lemon-800",
  clementine: "bg-ds-clementine-50 text-ds-clementine-800",
  strawberry: "bg-ds-strawberry-50 text-ds-strawberry-800",
  pitaya: "bg-ds-pitaya-50 text-ds-pitaya-800",
  blueberry: "bg-ds-blueberry-50 text-ds-blueberry-800",
  blackberry: "bg-ds-blackberry-50 text-ds-blackberry-800",
  slate: "bg-ds-slate-50 text-ds-slate-800",
  // Black over the surface (Figma "color/opacity/default"), not a palette: a
  // keyword, or the type of a desktop article.
  neutral: "bg-ds-black/4 text-ds-black/46",
} as const satisfies Record<TagVariant, string>;

// Trailing affordance, modelled as a discriminated union so a chip cannot be
// both "loading" and carry a static icon at once:
//   - "idle":    renders `icon` after the text (pass `null` for no icon).
//   - "loading": renders an inline spinner instead and makes the chip
//                non-interactive (`pointer-events-none`), so a parent
//                link/anchor wrapper can't be clicked until the source is final
//                (e.g. while a chat answer is still streaming its citations).
type TagState =
  { state: "idle"; icon: React.ReactNode | null } | { state: "loading" };

/**
 * Small content-type / label chip (Figma "Tag").
 * Tinted background with matching text, used for article-type metadata.
 */
function Tag(
  props: {
    children: React.ReactNode;
    size: TagSize;
    variant: TagVariant;
    className?: string;
  } & TagState
) {
  const { children, size, variant, className } = props;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded font-medium whitespace-nowrap",
        sizeClasses[size],
        variantClasses[variant],
        props.state === "loading" && "pointer-events-none opacity-60",
        className
      )}
    >
      {children}
      {props.state === "loading" ? (
        <IconLoader2 stroke={1.5} className="size-3 shrink-0 animate-spin" />
      ) : (
        props.icon
      )}
    </span>
  );
}

export { Tag };
